import "./PageHero.css";
import { useLayoutEffect, useRef } from "react";

// Fraction of the hero width used as the "line cap". Below this the lines
// overhang the title; at/above it the lines are flush with the title.
const LINE_CAP_FRACTION = 0.35;
// Maximum overhang per side, expressed in em (relative to the hero font-size).
const MAX_OVERHANG_EM = 0.75;

// Full-bleed page banner: background image with a centered title framed by a
// top and bottom line. Height is 3x the header (via --header-height). The line
// length follows the title:
//   overhang(each side) = clamp(0, (cap - textWidth) / 2, MAX_OVERHANG)
//   line length         = textWidth + 2 * overhang
// so short titles get a slight overhang, and once the title reaches the cap the
// overhang shrinks to 0 and the lines sit flush with the text. CSS can't do this
// (it can't read the glyph width), so the overhang is computed here.
export default function PageHero({ image, title }) {
    const heroRef = useRef(null);
    const frameRef = useRef(null);
    const titleRef = useRef(null);

    useLayoutEffect(() => {
        const hero = heroRef.current;
        const frame = frameRef.current;
        const titleEl = titleRef.current;
        if (!hero || !frame || !titleEl) return;

        const update = () => {
            const fontPx = parseFloat(getComputedStyle(frame).fontSize) || 16;
            const maxOverhang = MAX_OVERHANG_EM * fontPx;
            const cap = LINE_CAP_FRACTION * hero.clientWidth;
            // title is `flex: none; white-space: nowrap`, so its box == glyph width
            const textWidth = titleEl.getBoundingClientRect().width;
            const overhang = Math.max(
                0,
                Math.min((cap - textWidth) / 2, maxOverhang)
            );
            frame.style.paddingLeft = `${overhang}px`;
            frame.style.paddingRight = `${overhang}px`;
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(hero);
        // Recompute once the web font finishes loading (glyph widths change).
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(update);
        }
        return () => ro.disconnect();
    }, [title]);

    return (
        <div
            className="page-hero"
            ref={heroRef}
            style={{ backgroundImage: `url(${image})` }}
        >
            <div className="page-hero__frame" ref={frameRef}>
                <p className="page-hero__title" ref={titleRef}>
                    {title}
                </p>
            </div>
        </div>
    );
}
